// Step 5 — Automation: the transaction itself (identity, adoption, sync policy), then rules, then safety limits.
import { ReactNode } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Trash } from 'iconsax-react';

import type { AutomationRule, Condition, IdentitySpec, Operator, RuleAction, RuleEvent, SyncSpec, UpdatePolicy } from '../../types';
import type { StepProps } from '../stepProps';
import { CatalogueFieldPicker, MONO, PanelHead, SubCard, UPDATE_POLICY_LABEL, mappedProcess, newUid } from './SharedParts';

type Channel = 'IN-APP' | 'EMAIL' | 'SMS';
const CHANNELS: Array<{ v: Channel; l: string }> = [
  { v: 'IN-APP', l: 'In the app' },
  { v: 'EMAIL', l: 'Email' },
  { v: 'SMS', l: 'SMS' }
];
const EVENTS: Array<{ v: RuleEvent; l: string }> = [
  { v: 'TRANSACTION_CREATED', l: 'a transaction is created' },
  { v: 'TRANSACTION_FLAGGED', l: 'a transaction is flagged' },
  { v: 'FIELD_CHANGED', l: 'a mapped field changes in Aljex' },
  { v: 'RECORD_DELETED', l: 'the load is deleted in Aljex' }
];
const ACTIONS: Array<{ v: RuleAction['type']; l: string }> = [
  { v: 'NOTIFY', l: 'Notify' },
  { v: 'FLAG_MISSING_FIELDS', l: 'Flag missing fields' },
  { v: 'CANCEL_TRANSACTION', l: 'Cancel the transaction' }
];
const COND_OPS: Array<{ v: Operator; l: string; needsValue: boolean }> = [
  { v: 'is_empty', l: 'is empty', needsValue: false },
  { v: 'is_not_empty', l: 'is not empty', needsValue: false },
  { v: 'eq', l: 'is', needsValue: true },
  { v: 'neq', l: 'is not', needsValue: true }
];
const DEFAULT_ADOPT_PATTERN = '(?:^|\\D)(\\d{6})$';

const Row = ({ term, children }: { term: string; children: ReactNode }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '190px minmax(0, 1fr)', gap: 2, alignItems: 'baseline', py: 1.25 }}>
    <Typography variant="subtitle2" color="text.secondary">
      {term}
    </Typography>
    <Box>{children}</Box>
  </Box>
);

const Kw = ({ children }: { children: ReactNode }) => (
  <Typography
    component="span"
    variant="caption"
    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', width: 44, flexShrink: 0, pt: 1 }}
  >
    {children}
  </Typography>
);

export default function AutomationStep(props: StepProps) {
  const { spec, onChange, processes, lookups, catalogue } = props;
  const process = mappedProcess(spec, processes);
  const firstStepName = process?.firstStep?.name || 'the first step';

  // Target fields a rule can reference: the first step's fields, plus any bound field not in it.
  const targetFields: Array<{ uid: string; label: string }> = [
    ...(process?.firstStep?.fields || []).map((f) => ({ uid: f.uid, label: f.label })),
    ...spec.bindings
      .filter((b) => !(process?.firstStep?.fields || []).some((f) => f.uid === b.fieldUid))
      .map((b) => ({ uid: b.fieldUid, label: b.label }))
  ];
  const fieldLabel = (uid: string) => targetFields.find((f) => f.uid === uid)?.label || uid;
  const mappedUids = spec.bindings.filter((b) => b.owner === 'SOURCE').map((b) => b.fieldUid);

  const setIdentity = (patch: Partial<IdentitySpec>) => onChange({ ...spec, identity: { ...spec.identity, ...patch } });
  const setSync = (patch: Partial<SyncSpec>) => onChange({ ...spec, sync: { ...spec.sync, ...patch } });
  const setRules = (automation: AutomationRule[]) => onChange({ ...spec, automation });
  const setRule = (uid: string, patch: Partial<AutomationRule>) =>
    setRules(spec.automation.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  const setAction = (rule: AutomationRule, i: number, patch: Partial<RuleAction>) =>
    setRule(rule.uid, { actions: rule.actions.map((a, j) => (j === i ? { ...a, ...patch } : a)) });

  const adopt = spec.identity.adopt || null;
  const keySample = catalogue.find((c) => c.field === spec.identity.keyField)?.samples?.[0];
  const targetTeamUids =
    spec.target.mode === 'SINGLE' ? [spec.target.teamUid].filter(Boolean) : spec.target.routes.map((r) => r.teamUid || '').filter(Boolean);
  const overrides = spec.bindings.filter((b) => b.owner === 'SOURCE' && b.onUpdate && b.onUpdate !== spec.sync.defaultOnUpdate);

  const addRule = () =>
    setRules([
      ...spec.automation,
      {
        uid: newUid(),
        name: 'New rule',
        enabled: true,
        on: 'TRANSACTION_CREATED',
        when: null,
        actions: [{ type: 'NOTIFY', roleIds: [], channels: ['IN-APP'], message: '' }]
      }
    ]);

  const renderCondition = (rule: AutomationRule) => {
    const children = rule.when?.children || [];
    const cond = children.length === 1 && children[0].kind === 'condition' ? (children[0] as Condition) : null;
    if (children.length > 0 && !cond) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            This rule has a condition with {children.length} parts that can&apos;t be edited here.
          </Typography>
          <Button size="small" color="secondary" onClick={() => setRule(rule.uid, { when: null })}>
            Remove condition
          </Button>
        </Stack>
      );
    }
    if (!cond) {
      return (
        <Button
          size="small"
          onClick={() =>
            setRule(rule.uid, {
              when: {
                kind: 'group',
                op: 'AND',
                children: [{ kind: 'condition', field: targetFields[0] ? `target:${targetFields[0].uid}` : '', operator: 'is_empty' }]
              }
            })
          }
          disabled={targetFields.length === 0}
        >
          + Condition
        </Button>
      );
    }
    const setCond = (patch: Partial<Condition>) =>
      setRule(rule.uid, { when: { kind: 'group', op: 'AND', children: [{ ...cond, ...patch }] } });
    const op = COND_OPS.find((o) => o.v === cond.operator);
    const fieldUid = cond.field.startsWith('target:') ? cond.field.slice('target:'.length) : '';
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Select
          size="small"
          displayEmpty
          value={targetFields.some((f) => f.uid === fieldUid) ? fieldUid : ''}
          onChange={(e) => setCond({ field: `target:${String(e.target.value)}` })}
          inputProps={{ 'aria-label': 'Field' }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="" disabled>
            <em>Field</em>
          </MenuItem>
          {targetFields.map((f) => (
            <MenuItem key={f.uid} value={f.uid}>
              {f.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={op ? op.v : ''}
          displayEmpty
          onChange={(e) => {
            const next = COND_OPS.find((o) => o.v === e.target.value);
            setCond({ operator: e.target.value as Operator, value: next?.needsValue ? cond.value || '' : undefined });
          }}
          inputProps={{ 'aria-label': 'Operator' }}
        >
          {!op && (
            <MenuItem value="" disabled>
              <em>{cond.operator}</em>
            </MenuItem>
          )}
          {COND_OPS.map((o) => (
            <MenuItem key={o.v} value={o.v}>
              {o.l}
            </MenuItem>
          ))}
        </Select>
        {op?.needsValue && (
          <TextField size="small" placeholder="Value" value={cond.value || ''} onChange={(e) => setCond({ value: e.target.value })} />
        )}
        <Button size="small" color="secondary" onClick={() => setRule(rule.uid, { when: null })}>
          Remove
        </Button>
      </Stack>
    );
  };

  const renderAction = (rule: AutomationRule, a: RuleAction, i: number) => (
    <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'background.default' }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={a.type}
            onChange={(e) => {
              const type = e.target.value as RuleAction['type'];
              const next: RuleAction =
                type === 'NOTIFY'
                  ? { type, roleIds: a.roleIds || [], channels: a.channels || ['IN-APP'], message: a.message || '' }
                  : type === 'FLAG_MISSING_FIELDS'
                  ? { type, fieldUids: a.fieldUids || [], roleIds: a.roleIds }
                  : { type };
              setRule(rule.uid, { actions: rule.actions.map((x, j) => (j === i ? next : x)) });
            }}
            inputProps={{ 'aria-label': 'Action' }}
            sx={{ minWidth: 220 }}
          >
            {ACTIONS.map((x) => (
              <MenuItem key={x.v} value={x.v}>
                {x.l}
              </MenuItem>
            ))}
          </Select>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Remove action">
            <IconButton
              size="small"
              onClick={() => setRule(rule.uid, { actions: rule.actions.filter((_, j) => j !== i) })}
              aria-label="Remove action"
            >
              <Trash size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
        {a.type === 'NOTIFY' && (
          <>
            <Autocomplete
              multiple
              size="small"
              options={lookups.roles.map((r) => r._id)}
              value={a.roleIds || []}
              onChange={(_, v) => setAction(rule, i, { roleIds: v })}
              getOptionLabel={(id) => lookups.roles.find((r) => r._id === id)?.name || id}
              renderInput={(params) => <TextField {...params} label="Roles" placeholder="e.g. Operations" />}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                By
              </Typography>
              {CHANNELS.map((c) => {
                const on = (a.channels || []).includes(c.v);
                return (
                  <Chip
                    key={c.v}
                    size="small"
                    label={c.l}
                    color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'}
                    onClick={() =>
                      setAction(rule, i, {
                        channels: on ? (a.channels || []).filter((x) => x !== c.v) : [...(a.channels || []), c.v]
                      })
                    }
                    aria-pressed={on}
                  />
                );
              })}
            </Stack>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={1}
              label="Message"
              value={a.message || ''}
              onChange={(e) => setAction(rule, i, { message: e.target.value })}
              placeholder="New high-value load {{pro}} — complete the Operations details step"
              helperText="{{pro}} is replaced with the PRO number; {{field:<id>}} with a mapped value."
            />
          </>
        )}
        {a.type === 'FLAG_MISSING_FIELDS' && (
          <Autocomplete
            multiple
            size="small"
            options={targetFields.map((f) => f.uid)}
            value={a.fieldUids || []}
            onChange={(_, v) => setAction(rule, i, { fieldUids: v })}
            getOptionLabel={(uid) => fieldLabel(uid)}
            renderInput={(params) => <TextField {...params} label="Fields to flag for Operations" />}
          />
        )}
        {a.type === 'CANCEL_TRANSACTION' && (
          <Typography variant="body2" color="text.secondary">
            Cancels the linked transaction.
          </Typography>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={3}>
      <PanelHead title="What happens next" subtitle="First the transaction itself, then any rules you add." />

      <SubCard title="Transaction">
        <Box sx={{ '& > div + div': { borderTop: 1, borderColor: 'divider' } }}>
          <Row term="New match">
            <Typography variant="body2">
              Create a transaction and complete <b>{firstStepName}</b>
            </Typography>
          </Row>
          <Row term="Transaction key">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CatalogueFieldPicker
                catalogue={catalogue}
                value={spec.identity.keyField}
                onChange={(keyField) => setIdentity({ keyField })}
                width={220}
              />
              <Typography variant="caption" color="text.secondary">
                {spec.identity.keyField === 'id' ? 'the PRO number' : 'the value of this field'}
                {keySample ? `, e.g. ${keySample}` : ''}
              </Typography>
            </Stack>
          </Row>
          <Row term="Already tracked">
            <Stack spacing={1.25}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!adopt}
                    onChange={(e) =>
                      setIdentity({
                        adopt: e.target.checked
                          ? { pattern: DEFAULT_ADOPT_PATTERN, group: 1, scopeTeamUids: targetTeamUids, elsewhere: 'FLAG' }
                          : null
                      })
                    }
                  />
                }
                label={<Typography variant="body2">Look for loads a person already created by hand</Typography>}
              />
              {adopt && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">Look for a key matching</Typography>
                    <TextField
                      size="small"
                      value={adopt.pattern}
                      onChange={(e) => setIdentity({ adopt: { ...adopt, pattern: e.target.value } })}
                      inputProps={{ 'aria-label': 'Key pattern', style: { fontFamily: MONO, fontSize: '0.85rem' } }}
                      sx={{ width: 220 }}
                    />
                    <Typography variant="body2">group</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={adopt.group}
                      onChange={(e) => setIdentity({ adopt: { ...adopt, group: Math.max(0, Number(e.target.value) || 0) } })}
                      inputProps={{ 'aria-label': 'Capture group', min: 0 }}
                      sx={{ width: 72 }}
                    />
                    <Typography variant="body2">in</Typography>
                  </Stack>
                  <Autocomplete
                    multiple
                    size="small"
                    options={processes.map((p) => p.uid)}
                    value={adopt.scopeTeamUids}
                    onChange={(_, v) => setIdentity({ adopt: { ...adopt, scopeTeamUids: v } })}
                    getOptionLabel={(uid) => processes.find((p) => p.uid === uid)?.name || uid}
                    renderInput={(params) => <TextField {...params} label="Processes to search" />}
                    sx={{ maxWidth: 560 }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">
                      Found in {process ? <b>{process.name}</b> : 'the target process'}: link to it. Found in another process:
                    </Typography>
                    <Select
                      size="small"
                      value={adopt.elsewhere}
                      onChange={(e) => setIdentity({ adopt: { ...adopt, elsewhere: e.target.value as 'SKIP' | 'FLAG' | 'CREATE' } })}
                      inputProps={{ 'aria-label': 'When the load is already tracked in another process' }}
                    >
                      <MenuItem value="FLAG">Flag it for Operations</MenuItem>
                      <MenuItem value="SKIP">Skip it</MenuItem>
                      <MenuItem value="CREATE">Create anyway</MenuItem>
                    </Select>
                  </Stack>
                </>
              )}
            </Stack>
          </Row>
          <Row term="Aljex updates a load">
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Select
                size="small"
                value={spec.sync.defaultOnUpdate}
                onChange={(e) => setSync({ defaultOnUpdate: e.target.value as UpdatePolicy })}
                inputProps={{ 'aria-label': 'When Aljex updates a load' }}
              >
                <MenuItem value="FILL_EMPTY">Fill empty fields only</MenuItem>
                <MenuItem value="SOURCE_WINS">Always use Aljex&apos;s value</MenuItem>
                <MenuItem value="CREATE_ONLY">Never update</MenuItem>
              </Select>
              {overrides.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {overrides.map((b) => `${b.label}: ${UPDATE_POLICY_LABEL[b.onUpdate as UpdatePolicy].toLowerCase()}`).join(' · ')}
                </Typography>
              )}
            </Stack>
          </Row>
          <Row term="Aljex deletes a load">
            <Select
              size="small"
              value={spec.sync.onDelete}
              onChange={(e) => setSync({ onDelete: e.target.value as SyncSpec['onDelete'] })}
              inputProps={{ 'aria-label': 'When Aljex deletes a load' }}
            >
              <MenuItem value="CANCEL">Cancel the transaction</MenuItem>
              <MenuItem value="NOTIFY_ONLY">Notify Operations only</MenuItem>
              <MenuItem value="IGNORE">Ignore it</MenuItem>
            </Select>
          </Row>
          <Row term="Required field empty">
            <Select
              size="small"
              value={spec.sync.missingRequired}
              onChange={(e) => setSync({ missingRequired: e.target.value as SyncSpec['missingRequired'] })}
              inputProps={{ 'aria-label': 'When a required field is empty' }}
            >
              <MenuItem value="CREATE_AND_FLAG">Create it and flag the field</MenuItem>
              <MenuItem value="HOLD">Hold it for review</MenuItem>
              <MenuItem value="SKIP">Skip the load</MenuItem>
            </Select>
          </Row>
        </Box>
      </SubCard>

      <SubCard
        title="Rules"
        action={
          <Button size="small" variant="outlined" onClick={addRule}>
            + Rule
          </Button>
        }
      >
        <Stack spacing={1.5}>
          {spec.automation.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No rules yet. Add one to notify people or flag fields when something happens.
            </Typography>
          )}
          {spec.automation.map((rule) => (
            <Paper key={rule.uid} variant="outlined" sx={{ p: 2, borderRadius: 2, opacity: rule.enabled ? 1 : 0.7 }}>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Switch
                    size="small"
                    checked={rule.enabled}
                    onChange={(e) => setRule(rule.uid, { enabled: e.target.checked })}
                    inputProps={{ 'aria-label': `Rule enabled: ${rule.name}` }}
                  />
                  <TextField
                    variant="standard"
                    value={rule.name}
                    onChange={(e) => setRule(rule.uid, { name: e.target.value })}
                    inputProps={{ 'aria-label': 'Rule name', style: { fontWeight: 600 } }}
                    sx={{ flex: 1 }}
                  />
                  <Tooltip title="Delete rule">
                    <IconButton
                      size="small"
                      onClick={() => setRules(spec.automation.filter((r) => r.uid !== rule.uid))}
                      aria-label={`Delete rule ${rule.name}`}
                    >
                      <Trash size={18} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Kw>When</Kw>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                    <Select
                      size="small"
                      value={rule.on}
                      onChange={(e) => {
                        const on = e.target.value as RuleEvent;
                        setRule(rule.uid, { on, fieldUids: on === 'FIELD_CHANGED' ? rule.fieldUids || [] : undefined });
                      }}
                      inputProps={{ 'aria-label': 'When' }}
                    >
                      {EVENTS.map((ev) => (
                        <MenuItem key={ev.v} value={ev.v}>
                          {ev.l}
                        </MenuItem>
                      ))}
                    </Select>
                    {rule.on === 'FIELD_CHANGED' && (
                      <Autocomplete
                        multiple
                        size="small"
                        options={mappedUids}
                        value={rule.fieldUids || []}
                        onChange={(_, v) => setRule(rule.uid, { fieldUids: v })}
                        getOptionLabel={(uid) => fieldLabel(uid)}
                        renderInput={(params) => (
                          <TextField {...params} placeholder={mappedUids.length ? 'Which fields' : 'Map a field from Aljex first'} />
                        )}
                        sx={{ minWidth: 300, flex: 1 }}
                      />
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Kw>If</Kw>
                  <Box sx={{ flex: 1 }}>{renderCondition(rule)}</Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Kw>Then</Kw>
                  <Stack spacing={1} sx={{ flex: 1 }}>
                    {rule.actions.map((a, i) => renderAction(rule, a, i))}
                    <Box>
                      <Button
                        size="small"
                        onClick={() =>
                          setRule(rule.uid, {
                            actions: [...rule.actions, { type: 'NOTIFY', roleIds: [], channels: ['IN-APP'], message: '' }]
                          })
                        }
                      >
                        + Action
                      </Button>
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </SubCard>

      <SubCard title="Safety limits">
        <Typography variant="body2" component="div" sx={{ lineHeight: 2.6 }}>
          Pause this pipeline if more than{' '}
          <TextField
            size="small"
            type="number"
            value={spec.guards.maxCreatesPerHour}
            onChange={(e) => onChange({ ...spec, guards: { ...spec.guards, maxCreatesPerHour: Math.max(1, Number(e.target.value) || 1) } })}
            inputProps={{ 'aria-label': 'Maximum new transactions per hour', min: 1 }}
            sx={{ width: 90, mx: 0.5, verticalAlign: 'middle' }}
          />{' '}
          transactions are created in an hour, or more than{' '}
          <TextField
            size="small"
            type="number"
            value={spec.guards.pauseOnErrorRatePct}
            onChange={(e) =>
              onChange({
                ...spec,
                guards: { ...spec.guards, pauseOnErrorRatePct: Math.max(1, Math.min(100, Number(e.target.value) || 1)) }
              })
            }
            inputProps={{ 'aria-label': 'Maximum failure percentage', min: 1, max: 100 }}
            sx={{ width: 80, mx: 0.5, verticalAlign: 'middle' }}
          />
          % of runs fail within 15 minutes. Records keep queueing while it&apos;s paused, so nothing is lost.
        </Typography>
      </SubCard>
    </Stack>
  );
}
